// HelpProvider — owns the per-panel help overlay state.
//
// One provider per simulator mount. Holds:
//   - which overlay is currently open (see HelpOverlay)
//   - which hotspot is selected (when overlay === "hotspot")
//   - whether the welcome card is showing (auto on first mount, then
//     suppressed by localStorage; can be reopened from the menu)
//   - the panelKind so child components don't have to thread it
//
// localStorage key: `help.welcome-seen.<panelKind>` = "1"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { HelpOverlay, PanelKind } from "./help-types";
import { getHelpData } from "./help-data";

interface HelpContextValue {
  panelKind: PanelKind;
  overlay: HelpOverlay;
  selectedHotspotId: string | null;
  welcomeVisible: boolean;
  openOverlay: (next: HelpOverlay) => void;
  closeOverlay: () => void;
  selectHotspot: (id: string | null) => void;
  showWelcome: () => void;
  dismissWelcome: () => void;
}

export const HelpContext = createContext<HelpContextValue | null>(null);

function welcomeStorageKey(kind: PanelKind): string {
  return `help.welcome-seen.${kind}`;
}

function readWelcomeSeen(kind: PanelKind): boolean {
  try {
    return localStorage.getItem(welcomeStorageKey(kind)) === "1";
  } catch {
    return false;
  }
}

function writeWelcomeSeen(kind: PanelKind): void {
  try {
    localStorage.setItem(welcomeStorageKey(kind), "1");
  } catch {
    // Safari private mode + quota — ignore
  }
}

interface HelpProviderProps {
  panelKind: PanelKind;
  children: ReactNode;
}

export function HelpProvider({ panelKind, children }: HelpProviderProps) {
  const [overlay, setOverlay] = useState<HelpOverlay>("none");
  const [selectedHotspotId, setSelectedHotspotId] = useState<string | null>(null);
  const [welcomeVisible, setWelcomeVisible] = useState(false);

  // First-mount auto-show: only if there's welcome copy *and* the user
  // hasn't seen it for this kind before.
  useEffect(() => {
    const data = getHelpData(panelKind);
    if (!data.welcome) return;
    if (readWelcomeSeen(panelKind)) return;
    setWelcomeVisible(true);
  }, [panelKind]);

  const openOverlay = useCallback((next: HelpOverlay) => {
    setOverlay(next);
    if (next !== "hotspot") setSelectedHotspotId(null);
  }, []);

  const closeOverlay = useCallback(() => {
    setOverlay("none");
    setSelectedHotspotId(null);
  }, []);

  const selectHotspot = useCallback((id: string | null) => {
    setSelectedHotspotId(id);
  }, []);

  const showWelcome = useCallback(() => {
    setWelcomeVisible(true);
    setOverlay("none");
  }, []);

  const dismissWelcome = useCallback(() => {
    setWelcomeVisible(false);
    writeWelcomeSeen(panelKind);
  }, [panelKind]);

  const value = useMemo<HelpContextValue>(
    () => ({
      panelKind,
      overlay,
      selectedHotspotId,
      welcomeVisible,
      openOverlay,
      closeOverlay,
      selectHotspot,
      showWelcome,
      dismissWelcome,
    }),
    [
      panelKind,
      overlay,
      selectedHotspotId,
      welcomeVisible,
      openOverlay,
      closeOverlay,
      selectHotspot,
      showWelcome,
      dismissWelcome,
    ],
  );

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

export function useHelp(): HelpContextValue {
  const ctx = useContext(HelpContext);
  if (!ctx) {
    throw new Error("useHelp must be used inside a HelpProvider");
  }
  return ctx;
}
